// src/encounter/lib/to-social-history-request.ts
// Build the wire bodies for the IPS-rail Social History section (coded type + quantity + date
// range + comment). REUSES the curl-verified CreateSocialHistoryBody / UpdateSocialHistoryBody
// contracts (patient-chart-api.ts, Andrew curl 2026-05-20) — same wire quirks the patients-module
// AddSocialHistoryEditor ships. Pure — mirrors the Flags + Allergy + Condition mappers (PROD-1269).
//
// Actions:
//  - add    → POST /api/Patients/CreateSocialHistory (CreateSocialHistoryBody; status 'Active';
//             code.category nested + duplicated top-level; comment null when empty; createdBy=user).
//  - edit   → PUT  /api/Patients/socialHistory/{id}  (UpdateSocialHistoryBody; code.category nulled,
//             category lifted top-level; comment '' when empty; round-trips ORIGINAL createdBy +
//             performerDetails + speciality + category).
//  - delete → PUT  /api/Patients/socialHistory/{id}  (isDeleted:true; round-trips the record).
//
// ⚠️ STRICT RECORDER GUARD (mirrors AddSocialHistoryEditor): Social History's `createdBy` is the
// ORIGINAL recording user (audit trail). edit/delete return null when the original record is
// missing its `createdBy` (or its identifier) rather than silently substituting the editing user.
import type {
  CreateSocialHistoryBody,
  PatientSocialHistory,
  PerformerDetailsFull,
  SocialHabitQty,
  SocialHistoryCodeRow,
  UpdateSocialHistoryBody,
} from '@/types/patient-chart-api';
import type { CodeWordCase } from '@/types/patient-api';

export interface SocialHistoryRequestContext {
  /**
   * The encounter this item is being recorded IN — stamped on CREATE as the wire `encounter`.
   *
   * FLUTTER PARITY. Its shared clinical-info popup applies `data.encounter = encounterId` to EVERY
   * summary type before dispatching per type (`popup_widget.dart:152`), and the per-tab add paths do
   * the same (`allergies_tab.dart:785`, `history_tab.dart:1318,1490` → `encounter:
   * widget.encounterId`). Nearly every patient model carries the field — allergies, immunization,
   * conditions, procedures, medications, labs, flags, medical + social history.
   *
   * PA sent a hardcoded `encounter: null` here, so an item added on the pre-encounter had no record
   * of which visit produced it. Andrew, 2026-08-28: "when i call any api in the pre-encounter for
   * allergy, vaccine, family history, social history,.... we should add encounterId value."
   *
   * Optional on purpose. These builders are ENCOUNTER-ONLY — the patients-module chart has its own
   * path (`patients/services/use-create-allergy.ts` etc.), so an earlier version of this comment
   * claiming they "also serve the chart" was wrong. The reason it stays optional is that the
   * encounter itself can lack an id: a deep-link with no `createContext`, a Booked appointment
   * (where the open-phase create is deliberately gated), or an open-phase create that failed. In
   * those the rail still renders and still writes. Absent → `null`, exactly the previous behaviour.
   */
  encounterId?: string | null;
  patientId: string;
  /** Current user's userId GUID — stamped as `createdBy` on CREATE only. */
  userId: string;
}

/** The dialog's resolved selection. */
export interface SocialHistoryFormValue {
  /** Selected observation type (master GetSocialHistoryList row — carries the nested category). */
  code: SocialHistoryCodeRow;
  /** Selected quantity/frequency (master GetSocialHabitQtyList row, verbatim incl PascalCase
   *  `Definition`). Null when the master list hasn't resolved a pick. */
  historyCode: SocialHabitQty | null;
  /** ISO 8601 no-Z start + its display partner. */
  fromTime: string | null;
  fromDisplay: string | null;
  /** ISO 8601 no-Z end (null when ongoing) + its display partner. */
  toTime: string | null;
  toDisplay: string | null;
  comment?: string | null;
}

export type SocialHistoryAction =
  | { type: 'add'; value: SocialHistoryFormValue }
  | { type: 'edit'; existing: PatientSocialHistory; value: SocialHistoryFormValue }
  | { type: 'delete'; existing: PatientSocialHistory };

export interface SocialHistoryRequest {
  kind: 'create' | 'update';
  /** Social-history GUID for the update path (PUT /socialHistory/{id}); undefined on create. */
  socialHistoryId?: string;
  body: CreateSocialHistoryBody | UpdateSocialHistoryBody;
}

/** A plain Coding (NO nested category) — the Update wire shape for `code`. */
function plainCode(code: SocialHistoryCodeRow): SocialHistoryCodeRow {
  return {
    code: code.code ?? null,
    display: code.display ?? null,
    system: code.system ?? null,
    category: null,
  };
}

/** `historyCode` round-trips verbatim. The read-side surfaces a `CodeWordCase`; coerce to the
 *  SocialHabitQty wire shape (adds the PascalCase `Definition`, present-with-null on the wire). */
function normalizeHistoryCode(
  hc: SocialHabitQty | CodeWordCase | null | undefined,
): SocialHabitQty | null {
  if (!hc) return null;
  const definition = 'Definition' in hc ? (hc.Definition ?? null) : null;
  return {
    code: hc.code ?? null,
    Definition: definition,
    display: hc.display ?? null,
    system: hc.system ?? null,
    description: hc.description ?? null,
    shortName: hc.shortName ?? null,
  };
}

/** Round-trip the original performerDetails into the full wire shape. */
function normalizePerformerDetails(
  pd: PatientSocialHistory['performerDetails'],
): PerformerDetailsFull | null {
  if (!pd) return null;
  return {
    id: pd.id ?? null,
    name: pd.name ?? null,
    photo: pd.photo ?? null,
    speciality: null,
    timeZone: null,
    organizationId: null,
    roleName: null,
  };
}

/** CREATE body. status always 'Active'.
 *  ⚠️ Two non-obvious contract quirks — both mirror the curl-verified chart editor
 *  (add-social-history-editor.tsx, Andrew 2026-05-20); do NOT "simplify" either:
 *   • `category` is carried BOTH nested under `code` AND duplicated at top-level. (UPDATE nulls
 *     the nested copy and keeps only the top-level one — see buildUpdateSocialHistoryBody.)
 *   • `comment` ships `null` when empty on CREATE, but `""` when empty on UPDATE — the
 *     create/edit asymmetry is intentional wire parity, not a bug. */
export function buildCreateSocialHistoryBody(
  value: SocialHistoryFormValue,
  ctx: SocialHistoryRequestContext,
): CreateSocialHistoryBody {
  const nestedCategory: CodeWordCase | null = value.code.category ?? null;
  return {
    identifier: null,
    code: {
      code: value.code.code ?? null,
      display: value.code.display ?? null,
      system: value.code.system ?? null,
      category: nestedCategory,
    },
    status: 'Active',
    category: nestedCategory,
    comment: value.comment?.trim() || null,
    subject: ctx.patientId,
    encounter: ctx.encounterId ?? null,
    effectiveDateTime: null,
    fromDisplayTime: value.fromDisplay,
    fromTime: value.fromTime,
    toDisplayTime: value.toDisplay,
    toTime: value.toTime,
    historyCode: normalizeHistoryCode(value.historyCode),
    dataAbsentReason: 'n/a',
    isDeleted: false,
    createdBy: ctx.userId,
    createdDate: null,
    updatedby: null,
    updatedDate: null,
    performerDetails: null,
    speciality: null,
  };
}

/** Shared EDIT/DELETE body — round-trips the original record. `code.category` is nulled and the
 *  Coding lifted to top-level `category` (from the original record, or the picker when retyped). */
function buildUpdateSocialHistoryBody(
  existing: PatientSocialHistory,
  value: SocialHistoryFormValue | undefined,
  /** `encounterId`: the encounter this edit is happening IN — re-stamped onto the record,
   *  Flutter-style (see the field note below). Absent outside an encounter. */
  opts: { isDeleted: boolean; encounterId?: string | null },
): UpdateSocialHistoryBody {
  const code: SocialHistoryCodeRow = value
    ? plainCode(value.code)
    : {
        code: existing.code?.code ?? null,
        display: existing.code?.display ?? null,
        system: existing.code?.system ?? null,
        category: null,
      };
  // Top-level category: original record wins; on retype fall back to the picker row's nested cat.
  const topLevelCategory: CodeWordCase | null =
    existing.category ?? (value ? value.code.category ?? null : null);
  const historyCode = value ? value.historyCode : existing.historyCode;
  return {
    identifier: existing.identifier!,
    code,
    status: existing.status === 'Inactive' ? 'Inactive' : 'Active',
    category: topLevelCategory,
    comment: value ? value.comment?.trim() ?? '' : existing.comment ?? '',
    subject: existing.subject ?? '',
    /**
     * The CURRENT encounter wins, falling back to the record's own.
     *
     * FLUTTER PARITY (Andrew, 2026-08-28: "we will do as flutter"). Its shared popup assigns
     * `data.encounter = encounterId` BEFORE branching on `identifier != null`
     * (`popup_widget.dart:152`), so an edit re-stamps the item with the encounter it is being
     * edited IN — not the one that first recorded it. An earlier pass here preserved
     * `existing.encounter` on the reasoning that editing an old allergy shouldn't re-attribute it;
     * that was my call, and Andrew overruled it. It also fixes items PA wrote before the create-path
     * fix: they carry `encounter: null` and the next edit now heals them, where round-tripping would
     * have kept them null forever.
     *
     * THE `?? existing.encounter` IS NOT DECORATION. Flutter can assign unconditionally because its
     * popup guarantees an id first (`if (isBlank(encounterId)) createEncounter()`, initState). We
     * have no such guarantee: a deep-link with no `createContext`, a Booked appointment (open-phase
     * create gated), or a failed open-phase create all leave `encounterId` null while the rail still
     * renders and still writes. Assigning unconditionally in those cases would write null over a
     * real link and DESTROY the record's provenance. So: re-stamp when we have an encounter,
     * preserve when we don't.
     *
     * (An earlier version of this note justified the fallback with the patients-module chart. That
     * was wrong — the chart has its own request path and never calls these builders.)
     */
    encounter: opts.encounterId ?? existing.encounter ?? null,
    effectiveDateTime: null,
    fromDisplayTime: value ? value.fromDisplay : existing.fromDisplayTime ?? null,
    fromTime: value ? value.fromTime : existing.fromTime ?? null,
    toDisplayTime: value ? value.toDisplay : existing.toDisplayTime ?? null,
    toTime: value ? value.toTime : existing.toTime ?? null,
    historyCode: normalizeHistoryCode(historyCode),
    dataAbsentReason: 'n/a',
    isDeleted: opts.isDeleted,
    // Original recording user — preserved (the strict guard above ensures it's present).
    createdBy: existing.createdBy ?? null,
    createdDate: existing.createdDate ?? null,
    updatedby: null,
    updatedDate: null,
    performerDetails: normalizePerformerDetails(existing.performerDetails),
    speciality: existing.speciality ?? null,
  };
}

/**
 * Resolve a social-history action into endpoint + body.
 *  - add    → CREATE.
 *  - edit   → UPDATE (isDeleted:false).
 *  - delete → UPDATE soft-delete (isDeleted:true).
 * Returns null when the action is structurally invalid: edit/delete without an identifier, OR
 * without the ORIGINAL `createdBy` (strict recorder guard — never substitute the editing user).
 */
export function buildSocialHistoryRequest(
  action: SocialHistoryAction,
  ctx: SocialHistoryRequestContext,
): SocialHistoryRequest | null {
  if (action.type === 'add') {
    return { kind: 'create', body: buildCreateSocialHistoryBody(action.value, ctx) };
  }
  // edit / delete: require both the identifier AND the original recorder.
  if (!action.existing.identifier || !action.existing.createdBy) return null;

  if (action.type === 'delete') {
    return {
      kind: 'update',
      socialHistoryId: action.existing.identifier,
      body: buildUpdateSocialHistoryBody(action.existing, undefined, { isDeleted: true, encounterId: ctx.encounterId }),
    };
  }
  return {
    kind: 'update',
    socialHistoryId: action.existing.identifier,
    body: buildUpdateSocialHistoryBody(action.existing, action.value, { isDeleted: false, encounterId: ctx.encounterId }),
  };
}
